import { useMemo } from 'react';
import type {
  AccountApprovalAuditTrailEvent,
  AccountInviteAuditTrailEvent,
  AuditTrailEvent,
  AuditTrailEventResponse,
  ChildAccountInviteAuditTrailEvent,
  DueDiligenceDocumentStatusUpdateAuditTrailEvent,
  DueDiligenceRequirementStatusUpdateAuditTrailEvent,
  DueDiligenceSectionApprovalAuditTrailEvent,
  QuestionnaireRevisionRequestedAuditTrailEvent,
  QuestionnaireRevisionSubmittedAuditTrailEvent,
  RenameAccountAuditTrailEvent
} from '@gcv/shared';
import { AuditTrailAction } from '@gcv/shared';
import { useQuery } from '@tanstack/react-query';

import { BanksApi } from 'api/BanksApi';
import { BANK_DISPENSARY_AUDIT_TRAIL_EVENTS_KEY } from 'hooks/keys';
import { useUser } from 'hooks/queries';
import { sortArrayByField } from 'util/array.util';

//this builds a concise typing. array below is important
export type SupportedAuditTrailEventTypes =
  // ** Account Invite **
  | AccountApprovalAuditTrailEvent
  | ChildAccountInviteAuditTrailEvent
  | AccountInviteAuditTrailEvent
  | DueDiligenceDocumentStatusUpdateAuditTrailEvent
  | DueDiligenceRequirementStatusUpdateAuditTrailEvent
  | DueDiligenceSectionApprovalAuditTrailEvent
  // -- Questionnaire --
  // ** Questionnaire Revision **
  | QuestionnaireRevisionRequestedAuditTrailEvent
  | QuestionnaireRevisionSubmittedAuditTrailEvent
  // -- Account info --
  | RenameAccountAuditTrailEvent;

const supportedAuditTrailActionTypeSet = new Set<AuditTrailAction>([
  // ** Account Invite **
  AuditTrailAction.AccountApproval,
  AuditTrailAction.AccountInvite,
  AuditTrailAction.ChildAccountInvite,
  AuditTrailAction.DueDiligenceDocumentStatusUpdate,
  AuditTrailAction.DueDiligenceRequirementStatusUpdate,
  AuditTrailAction.DueDiligenceSectionApproval,
  // -- Questionnaire --
  // ** Questionnaire Revision **
  AuditTrailAction.QuestionnaireRevisionRequested,
  AuditTrailAction.QuestionnaireRevisionSubmitted,
  // -- Account Info
  AuditTrailAction.RenameAccount
] as SupportedAuditTrailEventTypes['action'][]);

const isSupportedAuditTrailEvent = (
  auditTrailEvent: AuditTrailEventResponse<AuditTrailEvent>
): auditTrailEvent is AuditTrailEventResponse<SupportedAuditTrailEventTypes> => {
  return supportedAuditTrailActionTypeSet.has(auditTrailEvent.action);
};

export function useBankDispensaryAuditTrailEvents(dispensaryId: string) {
  const { userBankId } = useUser();
  const banksApi = new BanksApi();

  const {
    data: auditTrailEvents,
    isLoading: isLoadingAuditTrailEvents,
    isError: isBankDispensaryAuditLogsError
  } = useQuery({
    enabled: !!userBankId.length && !!dispensaryId.length,
    queryKey: [BANK_DISPENSARY_AUDIT_TRAIL_EVENTS_KEY, userBankId, dispensaryId],
    queryFn: () => banksApi.getBankDispensaryAuditTrailEvents(userBankId, dispensaryId)
  });

  const sortedAndSupportedEvents: AuditTrailEventResponse<SupportedAuditTrailEventTypes>[] = useMemo(() => {
    return sortArrayByField(auditTrailEvents ?? [], 'event_date', false).filter(isSupportedAuditTrailEvent);
  }, [auditTrailEvents]);

  return {
    auditTrailEvents: sortedAndSupportedEvents,
    isLoadingAuditTrailEvents,
    isBankDispensaryAuditLogsError
  };
}
